import { useState } from "react";
import { type DepartmentDef, type RoleDef } from "../data/org";
import { RoleColumn } from "./RoleColumn";
import { useRH } from "../store/rh";
import { useCustomOrg } from "../store/customOrg";

export function Department({ dept, tone = "default" }:
  { dept: DepartmentDef; tone?: "default" | "council" | "management" }) {

  const customRoles = useCustomOrg(s => s.customRoles.filter(r => r.deptId === dept.id));
  const createRole  = useCustomOrg(s => s.createRole);
  const [addOpen, setAddOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newCap, setNewCap] = useState(1);

  // ROLES = nativas + customizadas convertidas pro mesmo shape
  const allRoles: (RoleDef & { custom?: boolean })[] = [
    ...dept.roles,
    ...customRoles.map(c => ({
      id: c.id, title: c.title, tier: "especialista" as const,
      capacity: c.capacity, custom: true
    }))
  ];

  const totalHired = useRH(s =>
    allRoles.reduce((acc, r) => acc + s.hiredAtSlot(`${dept.id}:${r.id}`).length, 0)
  );
  const totalCap = allRoles.reduce((a, r) => a + r.capacity, 0);

  const headerBg =
    tone === "council"    ? "bg-red text-bone" :
    tone === "management" ? "bg-ink text-bone" :
                            "bg-ink text-bone";
  const accent = tone === "council" ? "text-bone" : "text-gold";

  const submitNewRole = () => {
    if (!newTitle.trim()) return;
    createRole(dept.id, newTitle.trim(), newCap);
    setNewTitle(""); setNewCap(1); setAddOpen(false);
  };

  const cols = allRoles.length;

  return (
    <section className="border-2 border-ink bg-bone">
      <header className={`flex items-end justify-between px-5 py-4 ${headerBg}`}>
        <div>
          <div className={`font-mono text-xs ${accent}`}>{dept.code}</div>
          <h2 className="font-display font-black uppercase text-4xl leading-none tracking-tight">
            {dept.name}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setAddOpen(o => !o)}
            className="btn bg-bone text-ink border-bone hover:bg-gold">
            + vaga
          </button>
          <div className="text-right">
            <div className="font-mono text-[10px] uppercase opacity-70">ocupação</div>
            <div className="font-display text-3xl leading-none">
              {totalHired}/{totalCap}
            </div>
          </div>
        </div>
      </header>

      {addOpen && (
        <div className="bg-gold/20 border-b border-ink p-3 flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <div className="tag text-graydk mb-1">título do novo cargo</div>
            <input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="ex: Ghostwriter Sênior, UX Researcher…"
              className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[13px]"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") submitNewRole(); }}
            />
          </div>
          <div>
            <div className="tag text-graydk mb-1">capacidade</div>
            <input
              type="number" min={1} max={20}
              value={newCap}
              onChange={(e) => setNewCap(Number(e.target.value))}
              className="w-20 border border-ink bg-bone px-2 py-1 font-mono text-[13px]"
            />
          </div>
          <button onClick={submitNewRole} className="btn btn-hire">criar</button>
          <button onClick={() => setAddOpen(false)} className="btn btn-fire">cancelar</button>
        </div>
      )}

      <div className={`grid gap-3 p-3
        ${cols === 1 ? "grid-cols-1" :
          cols === 2 ? "grid-cols-1 md:grid-cols-2" :
          cols === 3 ? "grid-cols-1 md:grid-cols-3" :
                       "grid-cols-1 md:grid-cols-2 xl:grid-cols-4"}`}>
        {allRoles.map(r => (
          <RoleColumn key={r.id} dept={dept} role={r} custom={r.custom} />
        ))}
      </div>
    </section>
  );
}
